import {FemaGoModal, DateBoxField, Alert} from 'app-ui-componentlibrary';
import FundingOpportunityContext from './FundingOpportunityContext';
import _ from 'lodash';
import React, {useState, useContext} from 'react';
import SearchForCommitment from './SearchForCommitment';
import FundingOpportunityCommitmentDetails from './FundingOpportunityCommitmentDetails';
import {Header, Grid, Form} from 'semantic-ui-react';
import moment from 'moment-timezone';
import REST from 'go/requests/rest';

const AddFOCContainerModal = ({
  isOpen,
  closeModal,
  selectedCommitment,
  closeModalWithNewFoc,
}) => {
  const [isAddCommitmentSelected, setIsAddCommitmentSelected] = useState(
    selectedCommitment != null
  );

  const commitmentId = _.get(selectedCommitment, 'commitmentNumberId', null);

  const fundingOpportunity = useContext(FundingOpportunityContext);
  const liquidationDays = _.get(fundingOpportunity, 'configData.pop.liqOffset', 120);
  const foId = _.get(fundingOpportunity, 'fundingOpportunityId', null);
  const financialAbbreviationCodeId = _.get(
    fundingOpportunity,
    'financialAbbreviationCodeId',
    null
  );

  // state for fields entered by user
  const [region, setRegion] = useState('');
  const [doFundsExpire, setDoFundsExpire] = useState('Yes');
  const [appropriationEndDate, setAppropriationEndDate] = useState(null);
  const [popAmendmentCutoffDate, setPopAmendmentCutoffDate] = useState(null);
  // state for above fields error messages
  const [regionErrorMsg, setRegionErrorMsg] = useState(null);
  const [endDateErrorMsg, setEndDateErrorMsg] = useState(null);
  const [cutoffDateErrorMsg, setCutoffDateErrorMsg] = useState(null);
  const dropdownRequiredMsg = 'You must select a Region';
  const dateFieldMissingErrorMsg = 'Both dates are required if funds expire.';
  const cutoffDateBeforeEndDateErrorMsg = "POP amendment cutoff date cannot be earlier than Period of availability date.";

  const isEditable = true;

  const handleRegionChange = async (val) => {
    if (regionErrorMsg && (val == 0 || val)) {
      setRegionErrorMsg(null);
    }
    setRegion(val);
  };

  const handleAppropriationEndDateChange = async (e, valueObj) => {
    const val = _.get(valueObj, 'value', null);
    if (endDateErrorMsg) {
      setEndDateErrorMsg(null);
    }
    // request from BA Pia to clear out cutoff date value when changing end date so recalcuation is done again
    setPopAmendmentCutoffDate('');
    setAppropriationEndDate(val);
  };

  const handleAppropriationEndDateBlur = async (e) => {
    if (!popAmendmentCutoffDate) {
      /*
       * GMMGO-56949 AC 1: Automatically calculate the POP amendment cutoff date
       * the POP amendment cutoff date is populated based on the formula:
       * Period of availability end date plus 5 years minus liquidation period in days minus 1 month
       */
      let dfltCutoffDate = moment(appropriationEndDate)
        .add(5, 'years')
        .subtract(liquidationDays, 'days')
        .subtract(1, 'month')
        .format('YYYY-MM-DD');
      setPopAmendmentCutoffDate(dfltCutoffDate);
      setCutoffDateErrorMsg(null);
    }
  };

  const handlePopAmendmentCutoffDateChange = async (e, valueObj) => {
    const val = _.get(valueObj, 'value', null);
    if (cutoffDateErrorMsg) {
      setCutoffDateErrorMsg(null);
    }
    setPopAmendmentCutoffDate(val);
  };

  const handleSubmit = async () => {
    // save the FOC
    if (!validated()) {
      // we have some issues to be resolved before we can save the FOC
      return;
    }
    let commitmentRegion = parseInt(region);
    let payload = {
      commitmentNumbers: commitmentId,
      commitmentRegion,
      financialAbbreviationCodeId,
    };
    if (appropriationEndDate && popAmendmentCutoffDate) {
      payload = {
        ...payload,
        appropriationEndDate,
        popAmendmentCutoffDate,
      };
    }

    REST.fundingOpportunity
      .addFundingOpportunityCommitment(foId, payload)
      .then((res) => res.json())
      .then((foc) => {
        setRegion(null);
        setAppropriationEndDate(null);
        setPopAmendmentCutoffDate(null);
        closeModalWithNewFoc(foc);
      })
      .catch((error) => {
        console.error(
          'Call to REST.fundingOpportunity.addFundingOpportunityCommitment(foId, payload) failed.',
          {error}
        );
      });
  };

  const validated = () => {
    let isValid = true;
    if (region == 'N/A' || !region) {
      isValid = false;
      setRegionErrorMsg(dropdownRequiredMsg);
    }
    let endDate = moment(appropriationEndDate);
    let cutoffDate = moment(popAmendmentCutoffDate);
    if (doFundsExpire == 'Yes') {
      if (!appropriationEndDate) {
        isValid = false;
        setEndDateErrorMsg(dateFieldMissingErrorMsg);
      }
      if (!popAmendmentCutoffDate) {
        isValid = false;
        setCutoffDateErrorMsg(dateFieldMissingErrorMsg);
      } else if ( cutoffDate.isBefore(endDate) ) {
        isValid = false;
        setCutoffDateErrorMsg(cutoffDateBeforeEndDateErrorMsg);
      }
    }
    return isValid;
  };

  const closeAddCommitmentModal = async () => {
    setRegion(null);
    setAppropriationEndDate(null);
    setPopAmendmentCutoffDate(null);
    closeModal();
  };

  return (
    <FemaGoModal
      title="Add commitment to funding opportunity"
      open={isOpen}
      size="large"
      buttonText="Add Commitment"
      confirmAction={handleSubmit}
      cancelAction={closeAddCommitmentModal}
    >
      <div>
        <Grid>
          <header className="foc-compact-header">
            <h2>{fundingOpportunity.fundingOpportunityName}</h2>
            <h3>Program name: {fundingOpportunity.programName}</h3>
            <h3>Funding opportunity number: {fundingOpportunity.fundingOpportunityNr}</h3>
            <h3>Fiscal Year: {fundingOpportunity.fiscalYr}</h3>
          </header>

          <div>
            <FundingOpportunityCommitmentDetails
              isEditable={true}
              detailsData={selectedCommitment}
              region={region}
              handleRegionChange={handleRegionChange}
              regionErrorMsg={regionErrorMsg}
            />
          </div>
          <Alert info>
            <p>
              <b>
                Period of Availability End Date and POP Amendment Cutoff Date are required
                for funding opportunities where funds expire.</b> Once the period of availability ends, 
                the funds expire, and FEMA cannot make any new obligations from those funds. The period of availability 
                for the funding for any given financial assistance program will vary by the appropriation for that specific program.
            </p>
            <p>
              <b>
                Period of Availability End Date</b> is the date the appropriation expires.
                If this is not applicable, then leave both dates blank.
            </p>
            <p>
              <b>POP Amendment Cutoff Date </b> is the last day to which a grant's period
              of performance can be extended. A suggested date is provided based on the
              Period of Availability End Date and legal and policy guidance, but you can
              change the date to meet your program's need.
            </p>
            <p>
              If you are unsure about these dates, please reach out to the point of
              contact on the commitment in IFMIS before you add the commitment to FEMA GO
              because you will be unable to edit the details after you add it.
            </p>
            <p>
              Leaving these date fields empty when the appropriation actually expires
              could result in a grant having its period of performance extended beyond the
              date when funds are available which could result in Anti-Deficiency Act
              (ADA) issues.
            </p>
          </Alert>

          <div>
            {' '}
            Do the funds expire for this appropriation?
            <Form>
              <Form.Group id="subapplication-newOrCopy">
                <Form.Radio
                  className="radio-checkbox"
                  label="Yes"
                  title="Yes"
                  name="Yes"
                  option="Yes"
                  checked={doFundsExpire === 'Yes'}
                  value="Yes"
                  onChange={(e, {name, value}) => {
                    setDoFundsExpire(value);
                  }}
                />
                <br></br>
                <Form.Radio
                  className="radio-checkbox"
                  label="No"
                  title="No"
                  name="No"
                  checked={doFundsExpire === 'No'}
                  onChange={(e, {name, value}) => {
                    setDoFundsExpire(value);
                    setAppropriationEndDate(null);
                    setPopAmendmentCutoffDate(null);
                  }}
                  value="No"
                />
              </Form.Group>
            </Form>
          </div>

          {doFundsExpire == 'Yes' ? (
            <Grid.Row columns={2}>
              <Grid.Column width="8" verticalAlign="middle">
                {isEditable ? (
                  <DateBoxField
                    id="appropriationEndDate"
                    name="appropriationEndDate"
                    title="Period of availability end date"
                    label="Period of availability end date:"
                    handleChange={handleAppropriationEndDateChange}
                    handleBlur={handleAppropriationEndDateBlur}
                    value={appropriationEndDate}
                    errorMessages={[endDateErrorMsg]}
                    displayError={!_.isEmpty(endDateErrorMsg)}
                  />
                ) : (
                  _.get(detailsData, 'appropriationEndDate', 'N/A')
                )}
              </Grid.Column>
              <Grid.Column width="8" verticalAlign="middle">
                {isEditable ? (
                  <DateBoxField
                    id="popAmendmentCutoffDate"
                    name="popAmendmentCutoffDate"
                    title="POP amendment cutoff date"
                    label="POP amendment cutoff date:"
                    handleChange={handlePopAmendmentCutoffDateChange}
                    value={popAmendmentCutoffDate}
                    errorMessages={[cutoffDateErrorMsg]}
                    displayError={!_.isEmpty(cutoffDateErrorMsg)}
                  />
                ) : (
                  _.get(detailsData, 'popAmendmentCutoffDate', 'N/A')
                )}
              </Grid.Column>
            </Grid.Row>
          ) : (
            ''
          )}
        </Grid>
      </div>
    </FemaGoModal>
  );
};

export default AddFOCContainerModal;
